import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  IconButton,
  Avatar,
  Box,
  Grid,
} from "@mui/material";
import { showToast } from "../Toast/CommonToast";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import "./Profile.css";
import { changePassword } from "../../services/apiService";
import ReusableButton from "../Reusable/PrimaryButton/PrimaryButton";
import { Navigate, useNavigate } from "react-router-dom";

type FormErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
};

const ProfilePage = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const navigate = useNavigate();

  const mail = localStorage.getItem("email");
  const name = localStorage.getItem("name");
  let avatarName = "User";
  let initialsAvatar = "U";
  if (name && name !== "undefined" && name.trim() !== "") {
    avatarName = name.charAt(0).toUpperCase() + name.slice(1);
    initialsAvatar = name.charAt(0).toUpperCase();
  } else if (mail && mail.includes("@")) {
    const emailPrefix = mail.split("@")[0];
    avatarName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
    initialsAvatar = emailPrefix.charAt(0).toUpperCase();
  }

  // Check if passwords match
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  const validateForm = () => {
    let formErrors: FormErrors = {};

    if (!email.trim()) {
      formErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = "Invalid email format.";
    }
    if (!currentPassword.trim())
      formErrors.currentPassword = "Current password is required.";
    if (!newPassword.trim()) {
      formErrors.newPassword = "New password is required.";
    } else if (newPassword.length < 8) {
      formErrors.newPassword = "Password must be at least 8 characters.";
    }
    if (newPassword && newPassword !== confirmPassword) {
      formErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };
  const splitName = (name: string) => {
    const nameParts = name.trim().split(" ");
    return {
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" ") || "",
    };
  };

  useEffect(() => {
    if (avatarName) {
      const { firstName, lastName } = splitName(avatarName);
      setFirstName(firstName);
      setLastName(lastName);
    }
    if (mail) setEmail(mail);
  }, [avatarName, mail]);

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if(confirmPassword !== newPassword)
    {
      showToast("Current password and new password should not be same", "error");
      return;
    }
    try{
      const obj = {
      email : mail,
      currentPassword,
      newPassword,
    }
    const response = await changePassword(obj);
    if(response.status === 'true') {
      showToast("Password changed successfully!", "success");
      // navigate("/personass");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      navigate('/login');
      localStorage.clear();
    } else {
      showToast(response.message, "error");
    }
    }catch(error:any) {
      showToast(error?.message, "error");
    }
  };

  return (
    <Grid className="profile-page-container">
      <Box className="profile-page-box">
        <Typography variant="h1" gutterBottom className="studioNavHeading profile-title">My Profile</Typography>
        <Box className="profile-avatar-section">
          <IconButton>
            <Avatar className="profile-avatar">{initialsAvatar}</Avatar>
          </IconButton>
          <Box>
            <Typography className="showcaseSubTitle">{avatarName}</Typography>
            <Typography className="showcaseDescription">{mail}</Typography>
          </Box>
        </Box>

        <Box className="profile-info-section">
          <Typography className="showcaseDescription">Basic Info</Typography>
          <Grid container spacing={2} className="form-row">
            <Grid item xs={12} sm={12}>
              <TextField
                label="Name"
                id="outlined-disabled-name"
                // variant="outlined"
                value={firstName}
                disabled
                // onChange={(e) => setFirstName(e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                className="textField"
                style={{color:"white"}}
              />
            </Grid>
            {/* <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                variant="outlined"
                value={lastName}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                className="textField"
              />
            </Grid> */}
          </Grid>

          <Grid container spacing={2} className="form-row">
            <Grid item  xs={12} sm={12}>
              <TextField
                label="Email Address"
                id="outlined-disabled-email"
                disabled
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                className="textField"
              />
            </Grid>
          </Grid>

         {name !== 'undefined' && (
          <>
           <Typography className="showcaseDescription">Security</Typography>
          <Grid container spacing={2} className="form-row">
            <Grid item xs={12}>
              <TextField
                label="Current Password"
                variant="outlined"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
                fullWidth
                className="textField"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="New Password"
                variant="outlined"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                fullWidth
                className="textField"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      className="visibility-icon"
                    >
                      {showNewPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Confirm New Password"
                variant="outlined"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                fullWidth
                className="textField"
                InputProps={{
                  endAdornment: passwordsMatch ? (
                    <IconButton edge="end" className="visibility-icon" disabled>
                      <CheckIcon style={{ color: 'green' }} />
                    </IconButton>
                  ) : null,
                }}
              />
            </Grid>
          </Grid>
          <ReusableButton icon={<AutorenewIcon className="button-icon" />}   onClick={handleSubmit} disabled={
              !currentPassword || !newPassword || !confirmPassword}>
             Change Password
          </ReusableButton>
          </>
         )}
        </Box>
      </Box>
    </Grid>
  );
};

export default ProfilePage;